import { useLocation } from 'react-router-dom';
import type { AppContext } from '@/lib/core/types';
import type { CheckoutContentResponseModel } from '@/services/platform/api/model';
import { EventCheckoutScreen } from './EventCheckoutScreen';
import { MewsCheckoutScreen } from './MewsCheckoutScreen';
import { peekCheckoutKind } from './session';
import type { EventCheckoutState, MewsCheckoutState } from './types';

interface CheckoutScreenRouterProps {
  content: CheckoutContentResponseModel;
  context?: AppContext;
}

/**
 * One CMS `checkout` content type serves every paid-purchase context — today event bookings and
 * Mews bookings (sunbed, stay, the Mews branch of experience), both routed here by their own book
 * flow with the purchase in router state. Which adapter renders depends on which state key
 * arrived; when a redirect or a refresh destroyed router state entirely, it falls back to
 * whichever kind of session was last remembered, so `EventCheckoutScreen`/`MewsCheckoutScreen`
 * don't have to duplicate this decision to recover their own booking.
 */
export function CheckoutScreenRouter({ content, context }: CheckoutScreenRouterProps) {
  const location = useLocation();
  const state = location.state as {
    eventCheckout?: EventCheckoutState;
    mewsCheckout?: MewsCheckoutState;
  } | null;

  const isMews = !!state?.mewsCheckout || (!state?.eventCheckout && peekCheckoutKind() === 'mews');

  return isMews ? (
    <MewsCheckoutScreen content={content} context={context} />
  ) : (
    <EventCheckoutScreen content={content} context={context} />
  );
}
